import { defineRouter } from '#q-app';
import { Notify } from 'quasar';
import { createRouter, createWebHistory } from 'vue-router';
import routes from './routes';
import { useOAuthStore } from 'src/stores/oauth';
import { useUserSettingsStore } from 'src/stores/userSettings';
import { useUserStore } from 'src/stores/user';
import { useI18nStore } from 'src/stores/i18n';
import type { StaffPermissionSlug } from 'src/types/permissions';
import { devError, devWarn } from '@shared/vue/utils/debug';

export default defineRouter(function (/* { store, ssrContext } */) {
  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,
    history: createWebHistory(),
  });

  // ==========================================
  // Navigation Guards
  // ==========================================

  Router.beforeEach(async (to) => {
    const authStore = useOAuthStore();
    const userSettingsStore = useUserSettingsStore();
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    const requiresStaff = to.matched.some((record) => record.meta.requiresStaff);
    const requiredPermission = to.meta.permission as StaffPermissionSlug | undefined;

    userSettingsStore.setRouteLoading(true);

    // Preserve the intended destination as ?redirect= when bouncing to
    // login so deep links (email invitations, bookmarked drawer URLs)
    // survive the sign-in detour. LoginPage reads ?redirect= and pushes
    // there on success instead of falling back to /dashboard. Same
    // pattern as apps/ui/src/router/index.ts:51-56.
    const loginRedirect = () => {
      const target = to.fullPath;
      return target && target !== '/' && target !== '/login' && !target.startsWith('/?')
        ? { name: 'login' as const, query: { redirect: target } }
        : { name: 'login' as const };
    };

    try {
      if (requiresAuth && !authStore.hasToken) {
        const restored = await authStore.restoreSession();

        if (!restored) {
          userSettingsStore.setRouteLoading(false);
          return loginRedirect();
        }
      }

      if (requiresAuth && !authStore.isLoggedIn) {
        userSettingsStore.setRouteLoading(false);
        return loginRedirect();
      }

      // Staff interface: enforce the staff-role check whenever a route declares
      // requiresStaff. NOT gated on isLoggedIn — that left a latent bypass for
      // any future requiresStaff route that omits requiresAuth (the staff check
      // would simply be skipped). For current routes the auth check above has
      // already redirected unauthenticated users, so this is behaviour-preserving.
      if (requiresStaff) {
        const userStore = useUserStore();

        if (!userStore.isLoaded) {
          const fetched = await userStore.fetchUser();
          // A failed fetch (no session / transient network / 5xx) leaves
          // data=null → isStaff=false. Send the user to login (which offers a
          // retry) rather than misclassifying the blip as /unauthorized.
          if (!fetched) {
            userSettingsStore.setRouteLoading(false);
            return loginRedirect();
          }
        }

        if (!userStore.isStaff) {
          userSettingsStore.setRouteLoading(false);
          return '/unauthorized';
        }

        // Going to dashboard without dashboard.view permission falls through
        // to /unauthorized; otherwise send permission-less users to dashboard.
        if (requiredPermission && !userStore.hasPermission(requiredPermission)) {
          userSettingsStore.setRouteLoading(false);
          if (to.path === '/dashboard' || !userStore.hasPermission('staff.dashboard.view')) {
            return '/unauthorized';
          }
          return '/dashboard';
        }
      }

      // Login page is at '/' (root path), not '/login'
      if (!requiresAuth && authStore.isLoggedIn && to.path === '/') {
        userSettingsStore.setRouteLoading(false);
        return '/dashboard';
      }

      return true;
    } catch (error) {
      devError('❌ Router navigation failed:', error);
      userSettingsStore.setRouteLoading(false);

      // Fail closed for any gated route (auth OR staff): a thrown guard on a
      // protected page must bounce to login, never fall through to allow.
      return requiresAuth || requiresStaff ? loginRedirect() : true;
    }
  });

  let chunkLoadRetries = 0;
  const MAX_CHUNK_RETRIES = 2;

  // The reload budget has to outlive the page, otherwise a chunk that fails
  // permanently (a dependency missing from the dev container, a genuinely
  // broken build) becomes an endless reload loop: `chunkLoadRetries` is module
  // state, so every reload starts it back at 0 and the retry-then-reload path
  // repeats forever. sessionStorage records WHEN the last reload happened,
  // which caps it at one reload per cooldown while still letting a later
  // stale-chunk deploy self-heal on the next attempt.
  //
  // A timestamp rather than a flag cleared on success: the boot after a reload
  // rarely lands on the broken route directly (the guard bounces to login or
  // dashboard first), so clearing on any successful navigation would re-arm
  // the very loop this budget exists to stop.
  const CHUNK_RELOAD_KEY = 'si.chunkReloadAt';
  const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

  const chunkReloadAllowed = (): boolean => {
    try {
      const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
      return !last || Date.now() - last > CHUNK_RELOAD_COOLDOWN_MS;
    } catch {
      // Storage blocked (private mode / hardened profile): skip the reload
      // rather than risk the loop this budget exists to prevent.
      return false;
    }
  };

  // Returns whether the cooldown mark actually landed. A write that throws
  // (quota exhausted, a policy that permits reads but blocks writes) would
  // otherwise leave no mark at all, and reloading anyway would reinstate the
  // unbounded loop on the very next attempt.
  const markChunkReload = (): boolean => {
    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
      return true;
    } catch {
      return false;
    }
  };

  let dismissChunkFailureToast: (() => void) | null = null;

  const clearChunkFailureToast = (): void => {
    if (!dismissChunkFailureToast) return;

    dismissChunkFailureToast();
    dismissChunkFailureToast = null;
  };

  // Nothing is on screen when the FIRST navigation is the one that fails: the
  // layouts own the app chrome and they are themselves route components, so a
  // rejected chunk import leaves an empty <router-view /> and no spinner. A
  // sticky toast renders from the Quasar portal instead of the route tree, so
  // it is visible either way, and gives back the manual refresh the automatic
  // one just spent.
  const notifyChunkFailure = (): void => {
    const i18nStore = useI18nStore();

    clearChunkFailureToast();

    dismissChunkFailureToast = Notify.create({
      type: 'negative',
      // Sticky: on a first-navigation failure this toast is the only thing on
      // screen, so it must not time out before the operator sees it. Dismissable
      // because an in-app navigation failure leaves the previous page usable.
      timeout: 0,
      closeBtn: i18nStore.t('common.action.close'),
      message: i18nStore.t('common.error.pageLoadFailed'),
      actions: [
        {
          label: i18nStore.t('common.action.refresh'),
          color: 'white',
          handler: () => window.location.reload(),
        },
      ],
    });
  };

  Router.afterEach((to, from, failure) => {
    const userSettingsStore = useUserSettingsStore();

    if (!failure) {
      // Navigation worked, so the in-page retries below start fresh, and a
      // chunk-failure toast left over from an earlier attempt is now stale —
      // same clear-on-recovery contract as boot/api.ts's network-error toast.
      // The reload timestamp deliberately survives: see CHUNK_RELOAD_KEY.
      chunkLoadRetries = 0;
      clearChunkFailureToast();

      // Small delay to ensure smooth transition
      setTimeout(() => {
        userSettingsStore.setRouteLoading(false);
      }, 100);
    } else {
      userSettingsStore.setRouteLoading(false);
    }
  });

  // ==========================================
  // Error Handling
  // ==========================================

  Router.onError((error) => {
    const userSettingsStore = useUserSettingsStore();

    if (error.message.includes('Failed to fetch dynamically imported module')) {
      if (chunkLoadRetries < MAX_CHUNK_RETRIES) {
        chunkLoadRetries++;
        devWarn(
          `⚠️ Chunk loading failed, retrying (${chunkLoadRetries}/${MAX_CHUNK_RETRIES}):`,
          error
        );

        // Wait and retry navigation. Query and hash included: a staff deep link
        // carries the filter set (?user_uuid=…&page=…), and retrying the bare
        // pathname would silently drop it.
        setTimeout(() => {
          const { pathname, search, hash } = window.location;
          Router.replace(`${pathname}${search}${hash}`).catch((retryError) => {
            // A retry that fails with the same chunk error re-enters onError
            // and spends the next retry, so this only has to report.
            devError('❌ Chunk retry navigation failed:', retryError);
          });
        }, 1000);
      } else if (chunkReloadAllowed() && markChunkReload()) {
        devError(
          '❌ Chunk loading failed after retries, reloading page once:',
          error
        );
        userSettingsStore.setRouteLoading(false);
        window.location.reload();
      } else {
        devError(
          '❌ Chunk loading still failing after a reload, staying put:',
          error
        );
        userSettingsStore.setRouteLoading(false);
        notifyChunkFailure();
      }
    } else {
      devError('❌ Router error:', error);
      userSettingsStore.setRouteLoading(false);
    }
  });

  return Router;
});
